﻿import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import Draw, { createBox } from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import XTranslate from '../interaction/XTranslate';
import Select from 'ol/interaction/Select';
import * as featureInteractionTypes from '../utils/featureInteractionTypes';
import { createEmpty as createEmptyExtent, extend as extendExtent } from 'ol/extent';
import { createOlStyleFunction } from '../utils/style/canvas/OlStyler';
import { register } from 'ol/proj/proj4';
import { get as getProjection } from 'ol/proj';
import WKT from 'ol/format/WKT';
import proj4 from 'proj4';
import * as styleTypes from '../utils/style/styleTypes';
import * as layerTypes from '../utils/layerTypes';
import { click, always, primaryAction, never, noModifierKeys } from 'ol/events/condition';
import * as eventTypes from '../utils/eventTypes';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader';
import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter';
import OverlayOp from 'jsts/org/locationtech/jts/operation/overlay/OverlayOp.js';
import GeoJSON from 'ol/format/GeoJSON';
import { fromCircle } from 'ol/geom/Polygon';
import { getCenter, getHeight, getWidth } from 'ol/extent.js';

export default class FeatureInteractionHandler {
    constructor(mapControl) {
        this.mapControl = mapControl;
        this.map = mapControl.map;
        this.mapId = mapControl.mapId;
        this.classId = mapControl.classId;
        this.initiated = false;
        this.contourSnap = 'js-contour-snap-' + this.mapId + '-' + this.classId;
        this.keyDownListener = this._getKeyDownListener();
        this.lineStyle = new Style({
            stroke: new Stroke({
                color: 'red',
                width: 3
            })
        });
        this.polygonStyle = new Style({
            stroke: new Stroke({
                color: 'red',
                width: 2
            }),
            fill: new Fill({
                color: 'rgba(255,0,0,0.2)'
            })
        });
        $event.register(eventTypes.FINISH_GEOMETRY, this._getFinishGeometryHandler(), this, this.mapControl.classId);
        $event.register(eventTypes.RIGHT_CLICK, this._getFinishGeometryHandler(), this, this.mapControl.classId);
        $event.register(eventTypes.UNDO_VERTEX, this._getUndoVertexHandler(), this, this.mapControl.classId);
        $event.register(eventTypes.CONTOUR_SNAP_OFF, this._getContourSnapOffHandler(), this, this.mapControl.classId);
    }

    _getContourSnapOffHandler() {
        var handler = (eventName, data, sender) => {
            if (this.insertInteraction != null) {
                this.insertInteraction.deactivateTrace_();
            }
        };

        return handler;
    }

    _getFinishGeometryHandler() {
        var handler = (eventName, data, sender) => {
            if (this.insertInteraction != null) {
                this._guardedFinish(this.insertInteraction);
            }
        };

        return handler;
    }

    _guardedFinish(insertInteraction) {
        const sketch = insertInteraction.sketchFeature_;
        if (!sketch) {
            return;
        }
        const geom = sketch.getGeometry();
        const type = geom.getType();

        let coords;
        if (type === 'LineString') {
            coords = geom.getCoordinates();
        } else if (type === 'MultiLineString') {
            coords = geom.getLineStrings().reduce(
                (all, ls) => all.concat(ls.getCoordinates()), []
            );
        } else if (type === 'Polygon') {
            coords = geom.getLinearRing(0).getCoordinates();
        } else if (type === 'MultiPolygon') {
            coords = geom.getPolygons().reduce(
                (all, poly) => all.concat(poly.getLinearRing(0).getCoordinates()),
                []
            );
        } else {
            insertInteraction.abortDrawing();
            return;
        }

        const minLength = /LineString/.test(type) ? 3 : 4;

        if (!coords || coords.length < minLength) {
            insertInteraction.abortDrawing();
        } else {
            insertInteraction.finishDrawing();
        }
    }

    _getUndoVertexHandler() {
        var handler = (eventName, data, sender) => {
            if (this.insertInteraction != null) {
                this._removeLastVertex(this.insertInteraction);
            }
        };

        return handler;
    }

    _removeLastVertex(draw) {
        const coords = draw.sketchCoords_;
        if (!coords || coords.length < 2) {
            return;
        }

        coords.pop();

        const sketch = draw.sketchFeature_;
        const geom = sketch.getGeometry();
        const type = geom.getType();

        if (type === 'Polygon' || type === 'MultiPolygon') {
            geom.setCoordinates([coords]);
        } else {
            geom.setCoordinates(coords);
        }
    }

    revertEdit() {
        for (let i = 0; i < this.features.length; i++) {
            this.features[i].setGeometry(this.originalFeatures[i].getGeometry());
            this.features[i].set('xstate', 0);
        }
    }

    init(opts) {
        this.editMode = opts.editMode == null ? false : opts.editMode;
        this.layerId = opts.layerId;
        this.features = opts.features == null ? [] : opts.features;
        this.originalFeatures = [];

        this.features.forEach(x => {
            this.originalFeatures.push(x.clone());
        });

        this.insertCallback = opts.insertCallback;
        this.editCallback = opts.editCallback;
        this.currentAction = this.checkAction(opts.action);
        this.setProjs(this.mapControl.projectionHandler.projections);
        this.editSource = this.createEditSource();
        this.editLayer = this.createEditLayer(this.getOptionLayer(this.layerId));
        this.insertInteraction = null;
        this.editInteraction = null;
        this.selectInteraction = null;
        this.translateInteraction = null;
        this.previousAction = false;
        this.startInteraction(this.currentAction);
        this.initiated = true;
    }

    setProjs(projConfig) {
        let keys = Object.keys(projConfig);

        keys.forEach(key => {
            proj4.defs(projConfig[key].Id, projConfig[key].Def);
        });

        register(proj4);

        keys.forEach(key => {
            var projection = getProjection(projConfig[key].Id);
            projection.setExtent(projConfig[key].Extent);
        });
    }

    startInteraction(action, type) {
        if (this.previousAction) {
            this.clearPrevious();
        }

        if (this.features.length > 0) {
            this.oldBbox = this.featuresToBbox(this.features);
        }

        this.currentAction = action;

        this.features.forEach(x => { x.set('xstate', 5) });

        if (this.currentAction != null && (this.currentAction.SubType == 'ADD' || this.currentAction.SubType == 'ADD_MULTI')) {
            this.currentAction = featureInteractionTypes.EDIT;
        }

        switch (this.currentAction) {
            case featureInteractionTypes.INSERT:
                this.editMode && type == null ? this.startEditInteraction() : this.startInsertInteraction(type);
                break;
            case featureInteractionTypes.EDIT: this.startEditInteraction();
                break;
            case featureInteractionTypes.MAKE_HOLE: this.startMakeHoleInteraction(type);
                break;
            case featureInteractionTypes.MOVE: this.startMoveInteraction();
                break;
            case featureInteractionTypes.SCALE: this.startScaleInteraction();
                break;
            case featureInteractionTypes.ROTATE: this.startRotateInteraction();
                break;
            default: {
                this.previousAction = false;
                this.currentAction = null;
                throw 'Unknown interaction';
                break;
            }
        }
    }

    reverseVertices() {
        var result = {
            layerId: this.layerId,
            features: [],
            edit: this.editMode,
            oldBbox: this.oldBbox,
            newBbox: null
        };

        var bboxFeatures = [];

        this.editSource.forEachFeature((feature) => {
            if (feature.getGeometry().getType() == 'Polygon') {
                let coordinates = feature.getGeometry().getCoordinates();
                coordinates[0].reverse();
                feature.getGeometry().setCoordinates(coordinates);
            } else {
                feature.getGeometry().setCoordinates(feature.getGeometry().getCoordinates().reverse());
            }

            result.features.push(this.transformFeature(this.layerId, feature, this.editMode));
            bboxFeatures.push(feature);
        });

        result.newBbox = this.featuresToBbox(bboxFeatures);
        return result;
    }

    getFeatureInteractionResult(feature) {
        var result = {
            layerId: this.layerId,
            features: [],
            edit: this.editMode,
            oldBbox: this.oldBbox,
            newBbox: null
        };

        var bboxFeatures = [];

        result.features.push(this.transformFeature(this.layerId, feature, this.editMode));
        bboxFeatures.push(feature);

        result.newBbox = this.featuresToBbox(bboxFeatures);

        return result;
    }

    getInteractionResult() {
        var result = {
            layerId: this.layerId,
            features: [],
            edit: this.editMode,
            oldBbox: this.oldBbox,
            newBbox: null
        };

        var bboxFeatures = [];

        this.editSource.forEachFeature((feature) => {
            result.features.push(this.transformFeature(this.layerId, feature, this.editMode));
            bboxFeatures.push(feature);
        });

        result.newBbox = this.featuresToBbox(bboxFeatures);

        if (this.layerId == layerTypes.MEASUREMENTS || this.layerId == layerTypes.REDLINE) {
            this.features[0].setGeometry(this.editSource.getFeatures()[0].getGeometry());
            this.features[0].set('xstate', 0);
        }

        return result;
    }

    getLayerIdField(layerSettings) {
        let idField;

        if (layerSettings.Entity != null) {
            if (layerSettings.Entity.Type != 2) {
                idField = layerSettings.Entity.PkFieldName;
            } else {
                idField = layerSettings.Entity.PkField;
            }
        } else if (layerSettings.Geoservice != null) {
            idField = layerSettings.Geoservice.IdColumn;
        } else if (layerSettings.Type == layerTypes.TEMPORARY_VECTOR) {
            idField = layerSettings.idField;
        }

        return idField;
    }

    transformFeature(layerId, feature, editMode) {
        let clone = feature.clone();

        let layerProjection = this.mapControl.map.getView().getProjection().getCode();
        let layerConf = this.getOptionLayer(this.layerId);

        if (layerConf && layerConf.Entity) {
            layerProjection = this.getOptionLayer(this.layerId).Entity.GeometrySrid;
        }

        clone.getGeometry().transform(this.mapControl.generalSettingsHandler.settings.ProjCode, layerProjection);

        if (editMode) {
            let wkt = '';
            if (clone.getGeometry().getType() == 'Circle') {
                let poly = fromCircle(clone.getGeometry(), 64);
                wkt = new WKT().writeGeometry(poly);
            } else {
                wkt = new WKT().writeGeometry(clone.getGeometry());
            }

            var options = this.getOptionLayer(layerId);
            var id = clone.getProperties()[this.getLayerIdField(options)];
            return {
                geom: wkt,
                id: id,
                srid: new Number(layerProjection.replaceAll('EPSG:', ''))
            };
        } else {
            var wkt = new WKT().writeGeometry(clone.getGeometry());
            return {
                geom: wkt,
                srid: new Number(layerProjection.replaceAll('EPSG:', ''))
            };
        }
    }

    startMakeHoleInteraction(specificType) {
        this.previousAction = true;
        this.clearInsert();
        this.clearMove();
        this.clearEdit();

        var type = 'Polygon';
        var geometryFunction;

        if (specificType == 'square') {
            type = 'Circle';
            geometryFunction = createBox();
        }

        if (specificType == 'circle') {
            type = 'Circle';
        }

        if (specificType == 'polygon') {
            type = 'Polygon';
        }

        var trace = false;
        var traceSource;

        if (type == 'Polygon') {
            trace = true;
            traceSource = new VectorSource({
                features: this.mapControl.selectionHandler.getVisibleFeaturesInExtent(this.mapControl.getViewExtent())
            });
        }

        this.insertInteraction = new Draw({
            trace: trace,
            traceSource: traceSource,
            type: type,
            geometryFunction: geometryFunction,
            style: new Style({
                stroke: new Stroke({
                    color: 'red',
                    width: 2
                }),
                fill: new Fill({
                    color: 'rgba(0, 0, 0, 0)'
                })
            }),
            condition: function (evt) {
                return primaryAction(evt) && noModifierKeys(evt);
            },
            finishCondition: function (evt) {
                return primaryAction(evt);
            }
        });

        this.insertInteraction.on('drawend', (evt) => {
            if (specificType == 'circle') {
                let polygonGeom = fromCircle(evt.feature.getGeometry(), 64);
                evt.feature.setGeometry(polygonGeom);
            }

            let feature = evt.feature;
            let existing = this.editSource.getFeatures();

            if (existing.length > 0) {
                let clipped = this.clipPolygon(existing[0], feature);
                this.editSource.clear();
                this.editSource.addFeature(clipped);

                $('#feature-tool-edit')[0].click();
            }
        });

        this.map.addInteraction(this.insertInteraction);

        $event.notify(eventTypes.START_SNAP, { geomType: 'Polygon' }, this, this.mapControl.classId);
    }

    clipPolygon(featureA, featureB) {
        const gj = new GeoJSON({ featureProjection: this.mapControl.map.getView().getProjection().getCode(), dataProjection: this.mapControl.map.getView().getProjection().getCode() });

        const aFC = gj.writeFeatureObject(featureA);
        const bFC = gj.writeFeatureObject(featureB);

        const reader = new GeoJSONReader();
        const geomA = reader.read(aFC.geometry);
        const geomB = reader.read(bFC.geometry);

        const diff = OverlayOp.overlayOp(geomA, geomB, OverlayOp.DIFFERENCE);

        const writer = new GeoJSONWriter();
        const diffGeoJSON = {
            type: 'Feature',
            geometry: writer.write(diff),
            properties: {}
        };
        return gj.readFeature(diffGeoJSON);
    }

    startInsertInteraction(type) {
        this.previousAction = true;

        var layerConfig = this.getOptionLayer(this.layerId);

        this.addInsertInteraction(layerConfig, type);
        $event.notify(eventTypes.START_SNAP, { geomType: this.getDrawingType(layerConfig, null) }, this, this.mapControl.classId);
    }

    startEditInteraction() {

        this.previousAction = true;

        var layerConfig = this.getOptionLayer(this.layerId);

        this.addEditInteraction(layerConfig);
        $event.notify(eventTypes.START_SNAP, { geomType: this.getDrawingType(layerConfig, this.features) }, this, this.mapControl.classId);
    }

    calculateCenter(geometry) {
        let center, coordinates, minRadius;
        const type = geometry.getType();
        if (type === 'Polygon') {
            let x = 0;
            let y = 0;
            let i = 0;
            coordinates = geometry.getCoordinates()[0].slice(1);
            coordinates.forEach(function (coordinate) {
                x += coordinate[0];
                y += coordinate[1];
                i++;
            });
            center = [x / i, y / i];
        } else if (type === 'LineString') {
            center = geometry.getCoordinateAt(0.5);
            coordinates = geometry.getCoordinates();
        } else {
            center = getCenter(geometry.getExtent());
        }
        let sqDistances;
        if (coordinates) {
            sqDistances = coordinates.map(function (coordinate) {
                const dx = coordinate[0] - center[0];
                const dy = coordinate[1] - center[1];
                return dx * dx + dy * dy;
            });
            minRadius = Math.sqrt(Math.max.apply(Math, sqDistances)) / 3;
        } else {
            minRadius =
                Math.max(
                    getWidth(geometry.getExtent()),
                    getHeight(geometry.getExtent()),
                ) / 3;
        }
        return {
            center: center,
            coordinates: coordinates,
            minRadius: minRadius,
            sqDistances: sqDistances,
        };
    }

    startScaleInteraction() {
        this.previousAction = true;

        this.addScaleInteraction(this.getOptionLayer(this.layerId));
    }

    addScaleInteraction(layerConfig) {
        this.clearInsert();
        this.clearMove();
        this.clearEdit();

        this.selectInteraction = new Select({
            layers: [this.editLayer],
            style: this.handleStyleFunction(layerConfig),
            multi: true,
            condition: click,
            toggleCondition: always
        });

        this.selectInteraction.on('select', (evt) => {
            if (evt.selected.length > 0) {
                evt.selected.forEach(x => x.set('selected', 1));
            }

            if (evt.deselected.length > 0) {
                evt.deselected.forEach(x => x.set('selected', 0));

                if (this.editSource != null && this.editSource.getFeatures().length > 0) {
                    this.editSource.getFeatures()[0].set('selected', 1);
                    this.selectInteraction.getFeatures().push(this.editSource.getFeatures()[0]);
                }
            }
        });

        this.map.addInteraction(this.selectInteraction);

        this.editInteraction = new Modify({
            features: this.selectInteraction.getFeatures(),
            condition: (event) => {
                return primaryAction(event);
            },
            deleteCondition: never,
            insertVertexCondition: never,
            style: (feature, resolution) => {
                feature.get('features').forEach(modifyFeature => {
                    const modifyGeometry = modifyFeature.get('modifyGeometry');
                    if (modifyGeometry) {
                        const point = feature.getGeometry().getCoordinates();
                        let modifyPoint = modifyGeometry.point;
                        if (!modifyPoint) {
                            modifyPoint = point;
                            modifyGeometry.point = modifyPoint;
                            modifyGeometry.geometry0 = modifyGeometry.geometry;
                            const result = this.calculateCenter(modifyGeometry.geometry0);
                            modifyGeometry.center = result.center;
                            modifyGeometry.minRadius = result.minRadius;
                        }

                        const center = modifyGeometry.center;
                        const minRadius = modifyGeometry.minRadius;
                        let dx, dy;
                        dx = modifyPoint[0] - center[0];
                        dy = modifyPoint[1] - center[1];
                        const initialRadius = Math.sqrt(dx * dx + dy * dy);
                        if (initialRadius > minRadius) {
                            const initialAngle = Math.atan2(dy, dx);
                            dx = point[0] - center[0];
                            dy = point[1] - center[1];
                            const currentRadius = Math.sqrt(dx * dx + dy * dy);
                            if (currentRadius > 0) {
                                const currentAngle = Math.atan2(dy, dx);
                                const geometry = modifyGeometry.geometry0.clone();
                                geometry.scale(currentRadius / initialRadius, undefined, center);
                                modifyGeometry.geometry = geometry;
                            }
                        }
                    }
                });

                return this.handleStyleFunction(layerConfig)(feature);
            }
        });

        this.editInteraction.on('modifystart', (event) => {
            event.features.forEach((feature) => {
                feature.set(
                    'modifyGeometry',
                    { geometry: feature.getGeometry().clone() },
                    true,
                );
            });

            this.selectInteraction.getFeatures().forEach((feature) => {
                feature.set('hidden-state', 1);
            });
        });

        this.editInteraction.on('modifyend', (event) => {
            event.features.forEach((feature) => {
                const modifyGeometry = feature.get('modifyGeometry');
                if (modifyGeometry) {
                    feature.setGeometry(modifyGeometry.geometry);
                    feature.unset('modifyGeometry', true);
                }
            });

            this.selectInteraction.getFeatures().forEach((feature) => {
                feature.set('hidden-state', 0);
            });

            this.editCallback();
        });

        this.map.addInteraction(this.editInteraction);

        this.autoSelect();
    }

    startRotateInteraction() {
        this.previousAction = true;

        this.addRotateInteraction(this.getOptionLayer(this.layerId));
    }

    addRotateInteraction(layerConfig) {
        this.clearInsert();
        this.clearMove();
        this.clearEdit();

        this.selectInteraction = new Select({
            layers: [this.editLayer],
            style: this.handleStyleFunction(layerConfig),
            multi: true,
            condition: click,
            toggleCondition: always
        });

        this.selectInteraction.on('select', (evt) => {
            if (evt.selected.length > 0) {
                evt.selected.forEach(x => x.set('selected', 1));
            }

            if (evt.deselected.length > 0) {
                evt.deselected.forEach(x => x.set('selected', 0));

                if (this.editSource != null && this.editSource.getFeatures().length > 0) {
                    this.editSource.getFeatures()[0].set('selected', 1);
                    this.selectInteraction.getFeatures().push(this.editSource.getFeatures()[0]);
                }
            }
        });

        this.map.addInteraction(this.selectInteraction);

        this.editInteraction = new Modify({
            features: this.selectInteraction.getFeatures(),
            condition: (event) => {
                return primaryAction(event);
            },
            deleteCondition: never,
            insertVertexCondition: never,
            style: (feature, resolution) => {
                feature.get('features').forEach(modifyFeature => {
                    const modifyGeometry = modifyFeature.get('modifyGeometry');
                    if (modifyGeometry) {
                        const point = feature.getGeometry().getCoordinates();
                        let modifyPoint = modifyGeometry.point;
                        if (!modifyPoint) {
                            modifyPoint = point;
                            modifyGeometry.point = modifyPoint;
                            modifyGeometry.geometry0 = modifyGeometry.geometry;
                            const result = this.calculateCenter(modifyGeometry.geometry0);
                            modifyGeometry.center = result.center;
                            modifyGeometry.minRadius = result.minRadius;
                        }

                        const center = modifyGeometry.center;
                        const minRadius = modifyGeometry.minRadius;
                        let dx, dy;
                        dx = modifyPoint[0] - center[0];
                        dy = modifyPoint[1] - center[1];
                        const initialRadius = Math.sqrt(dx * dx + dy * dy);
                        if (initialRadius > minRadius) {
                            const initialAngle = Math.atan2(dy, dx);
                            dx = point[0] - center[0];
                            dy = point[1] - center[1];
                            const currentRadius = Math.sqrt(dx * dx + dy * dy);
                            if (currentRadius > 0) {
                                const currentAngle = Math.atan2(dy, dx);
                                const geometry = modifyGeometry.geometry0.clone();
                                geometry.rotate(currentAngle - initialAngle, center);
                                modifyGeometry.geometry = geometry;
                            }
                        }
                    }
                });

                return this.handleStyleFunction(layerConfig)(feature);
            }
        });

        this.editInteraction.on('modifystart', (event) => {
            event.features.forEach((feature) => {
                feature.set(
                    'modifyGeometry',
                    { geometry: feature.getGeometry().clone() },
                    true,
                );
            });

            this.selectInteraction.getFeatures().forEach((feature) => {
                feature.set('hidden-state', 1);
            });
        });

        this.editInteraction.on('modifyend', (event) => {
            event.features.forEach((feature) => {
                const modifyGeometry = feature.get('modifyGeometry');
                if (modifyGeometry) {
                    feature.setGeometry(modifyGeometry.geometry);
                    feature.unset('modifyGeometry', true);
                }
            });

            this.selectInteraction.getFeatures().forEach((feature) => {
                feature.set('hidden-state', 0);
            });

            this.editCallback();
        });

        this.map.addInteraction(this.editInteraction);

        this.autoSelect();
    }

    startMoveInteraction() {
        this.previousAction = true;

        this.addMoveInteraction(this.getOptionLayer(this.layerId));
    }

    addInsertInteraction(layerConfig, specificType) {
        this.editSource.clear();
        this.clearInsert();
        this.clearMove();
        this.clearEdit();

        var type = this.getDrawingType(layerConfig, null);
        var geometryName = this.getGeometryName(layerConfig);
        var geometryFunction = this.getGeometryFunction(layerConfig);
        var trace = false;
        var traceSource;

        if (type == 'Polygon' || type == 'MultiPolygon' || type == 'LineString' || type == 'MultiLineString') {
            trace = true;
            traceSource = new VectorSource({
                features: this.mapControl.selectionHandler.getVisibleFeaturesInExtent(this.mapControl.getViewExtent())
            }); 
        }

        if (specificType == 'square') {
            type = 'Circle';
            geometryFunction = createBox();
        }

        if (specificType == 'circle') {
            type = 'Circle';
        }

        if (specificType == 'polygon') {
            type = 'Polygon';
        }

        if (specificType == 'line') {
            this.insertInteraction = new Draw({
                source: this.editSource,
                type: type,
                maxPoints: 2,
                geometryFunction: geometryFunction,
                geometryName: geometryName,
                style: this.handleInsertStyleFunction(),
                condition: function (evt) {
                    return primaryAction(evt) && noModifierKeys(evt);
                },
                finishCondition: function (evt) {
                    return primaryAction(evt);
                }
            });
        } else if (type == 'Polygon' || type == 'MultiPolygon' || type == 'LineString' || type == 'MultiLineString') {
            this.insertInteraction = new Draw({
                trace: trace,
                traceSource: traceSource,
                source: this.editSource,
                type: type,
                geometryFunction: geometryFunction,
                geometryName: geometryName,
                style: this.handleInsertStyleFunction(),
                condition: function (evt) {
                    return primaryAction(evt) && noModifierKeys(evt);
                },
                finishCondition: function (evt) {
                    return primaryAction(evt);
                }
            });
        } else {
            this.insertInteraction = new Draw({
                source: this.editSource,
                type: type,
                geometryFunction: geometryFunction,
                geometryName: geometryName,
                style: this.handleInsertStyleFunction(),
                condition: function (evt) {
                    return primaryAction(evt) && noModifierKeys(evt);
                },
                finishCondition: function (evt) {
                    return primaryAction(evt);
                }
            });
        }

        if (type == "Point") {

            this.selectInteraction = new Select({
                layers: [this.editLayer],
                style: this.handleStyleFunction(layerConfig),
                multi: true,
                condition: click,
                toggleCondition: always
            });

            this.selectInteraction.on('select', (evt) => {
                if (evt.selected.length > 0) {
                    evt.selected.forEach(x => x.set('selected', 1));
                }

                if (evt.deselected.length > 0) {
                    evt.deselected.forEach(x => x.set('selected', 0));

                    if (this.editSource != null && this.editSource.getFeatures().length > 0) {
                        this.editSource.getFeatures()[0].set('selected', 1);
                        this.selectInteraction.getFeatures().push(this.editSource.getFeatures()[0]);
                    }
                }
            });

            this.map.addInteraction(this.selectInteraction);

            this.insertInteraction.on('drawstart', (evt) => {
                evt.feature.set('selected', 1)
                this.editSource.clear();
            });

            this.insertInteraction.on('drawend', (evt) => {

                evt.feature.layerId = this.layerId;

                this.selectInteraction.getFeatures().push(evt.feature);
                this.insertCallback(evt.feature);
            });
        }
        else {
            this.insertInteraction.on('drawstart', (evt) => {
                evt.feature.set('selected', 1)
            });

            this.insertInteraction.on('drawend', (evt) => {
                if (specificType == 'circle') {
                    let polygonGeom = fromCircle(evt.feature.getGeometry(), 64);
                    evt.feature.setGeometry(polygonGeom);
                }

                evt.feature.layerId = this.layerId;
                this.clearInsert();
                this.insertCallback(evt.feature);

                if (this.layerId == layerTypes.MEASUREMENTS || this.layerId == layerTypes.REDLINE) {
                    this.features[0].setGeometry(evt.feature.getGeometry());
                } else {
                    this.features = [evt.feature];
                }

                this.createEditSource();
                this.createEditLayer(layerConfig);
                this.addEditInteraction(this.getOptionLayer(this.layerId));
                $event.notify(eventTypes.START_SNAP, { geomType: this.getDrawingType(layerConfig, this.features) }, this, this.mapControl.classId);
                this.selectInteraction.getFeatures().push(evt.feature);
                $('#feature-tool-edit-specific').show();
                this.setButtonActivated('#feature-tool-edit-specific');
            });
        }

        this.map.addInteraction(this.insertInteraction);
    }

    setButtonActivated(selector) {
        $(selector).siblings('.active').removeClass('active').attr('enabled', '0');
        $(selector).removeClass('disabled');
        $(selector).addClass('active');
    }

    addEditInteraction(layerConfig) {
        this.clearInsert();
        this.clearMove();
        this.clearEdit();

        this.selectInteraction = new Select({
            layers: [this.editLayer],
            style: this.handleStyleFunction(layerConfig),
            multi: true,
            condition: click,
            toggleCondition: always
        });

        this.selectInteraction.on('select', (evt) => {
            if (evt.selected.length > 0) {
                evt.selected.forEach(x => x.set('selected', 1));
            }

            if (evt.deselected.length > 0) {
                evt.deselected.forEach(x => x.set('selected', 0));

                if (this.editSource != null && this.editSource.getFeatures().length > 0) {
                    this.editSource.getFeatures()[0].set('selected', 1);
                    this.selectInteraction.getFeatures().push(this.editSource.getFeatures()[0]);
                }
            }
        });

        this.map.addInteraction(this.selectInteraction);

        var type = this.getDrawingType(layerConfig, this.features);
        if (type == "Point") {
            var geometryName = this.getGeometryName(layerConfig);
            var geometryFunction = this.getGeometryFunction(layerConfig);

            this.insertInteraction = new Draw({
                source: this.editSource,
                type: type,
                geometryFunction: geometryFunction,
                geometryName: geometryName,
                style: this.handleInsertStyleFunction(),
                condition: function (evt) {
                    return primaryAction(evt) && noModifierKeys(evt);
                },
                finishCondition: function (evt) {
                    return primaryAction(evt);
                }
            });

            this.oldFeatureProperties = null;
            this.insertInteraction.on('drawstart', (evt) => {

                if (this.editSource.getFeatures().length > 0) {
                    this.oldFeatureProperties = this.editSource.getFeatures()[0].getProperties();
                }
                this.editSource.clear();
            });

            this.insertInteraction.on('drawend', (evt) => {
                evt.feature.layerId = this.layerId;
                if (this.oldFeatureProperties != null) {
                    var keys = Object.keys(this.oldFeatureProperties);
                    for (var i = 0; i < keys.length; i++) {
                        var key = keys[i];
                        if (this.oldFeatureProperties[key] != null && typeof this.oldFeatureProperties[key] != 'object') {
                            evt.feature.set(key, this.oldFeatureProperties[key]);
                        }
                    }
                }


                this.selectInteraction.getFeatures().push(evt.feature);
                this.editCallback();
            });

            this.map.addInteraction(this.insertInteraction);
            this.autoSelect();
        }
        else {

            this.editInteraction = new Modify({
                features: this.selectInteraction.getFeatures(),
                style: this.handleStyleFunction(layerConfig),
                deleteCondition: (evt) => {
                    return evt.originalEvent.button === 2;
                }
            });

            this.editInteraction.on('modifyend', (evt) => {
                this.editCallback();
            });

            this.map.addInteraction(this.editInteraction);
            this.autoSelect();
        }
    }

    _getKeyDownListener() {
        return (evt) => {
        };
    }

    addMoveInteraction(layerConfig) {
        this.clearInsert();
        this.clearMove();
        this.clearEdit();

        this.selectInteraction = new Select({
            layers: [this.editLayer],
            style: this.handleStyleFunction(layerConfig),
            multi: true,
            condition: click,
            toggleCondition: always
        });

        this.selectInteraction.on('select', (evt) => {
            if (evt.selected.length > 0) {
                evt.selected.forEach(x => x.set('selected', 1));
            }

            if (evt.deselected.length > 0) {
                evt.deselected.forEach(x => x.set('selected', 0));

                if (this.editSource != null && this.editSource.getFeatures().length > 0) {
                    this.editSource.getFeatures()[0].set('selected', 1);
                    this.selectInteraction.getFeatures().push(this.editSource.getFeatures()[0]);
                }
            }
        });

        this.map.addInteraction(this.selectInteraction);

        this.translateInteraction = new XTranslate({
            features: this.selectInteraction.getFeatures()
        });

        this.translateInteraction.on('translateend', (evt) => {
            this.editCallback();
        });

        this.map.addInteraction(this.translateInteraction);

        this.map.on('singleclick', (event) => {
            try {
                var layer = this.map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
                    return layer;
                });
                if (layer) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            } catch (err) {

            }
        });

        this.autoSelect();
    }

    autoSelect() {
        var existingFeatures = this.editSource.getFeatures();

        if (existingFeatures) {
            existingFeatures.forEach(feature => {
                feature.set('selected', 1);
                this.selectInteraction.getFeatures().push(feature);
            });
        }
    }

    getDrawingType(layerConfig, features) {
        var geometryType = this._getLayerGeometryType(layerConfig, features);

        if (geometryType == null || geometryType == '') {
            throw 'Config not found';
        }

        switch (geometryType) {
            case 'POINT': return "Point";
            case 'LINE': return "LineString";
            case 'POLYGON': return "Polygon";
            case 'MULTIPOINT': return "MultiPoint";
            case 'MULTILINE': return "MultiLineString";
            case 'MULTIPOLYGON': return "MultiPolygon";
            default: throw 'Geometry not supported';
        }
    }

    getGeometryName(layerConfig) {
        return null;
    }

    getGeometryFunction(layerConfig) {
        return null;
    }

    centerFeatures() {
        var selectedFeatures = this.editSource.getFeatures();
        if (selectedFeatures == null || selectedFeatures.length == 0) {
            return;
        }

        var maxResolution = this.mapControl.layerHandler.getLayerById(this.layerId)[0].get('maxResolution');
        var minResolution = this.mapControl.layerHandler.getLayerById(this.layerId)[0].get('minResolution');
        var resolution = this.map.getView().getResolution();
        if (maxResolution != null && resolution > maxResolution) {
            resolution = maxResolution;
        }
        else if (minResolution != null && resolution < minResolution) {
            resolution = minResolution;
        }

        this.map.getView().setResolution(resolution);
        
        var extent = createEmptyExtent();
        for (var i = 0; i < selectedFeatures.length; i++) {
            var geom = selectedFeatures[i].getGeometry();

            if (geom != null && geom.getExtent() != null) {
                extendExtent(extent, geom.getExtent());
            }
        }

        if (extent[0] == extent[2] && extent[1] == extent[3]) {
            this.map.getView().animate({
                center: [extent[0], extent[1]],
                resolution: resolution,
                duration: 2000
            });
        } else {
            this.map.getView().fit(extent, { minResolution: resolution, duration: 1000 });
        }
    }

    featuresToBbox(features) {
        var result = createEmptyExtent();

        for (var i = 0; i < features.length; i++) {
            var geom = features[i].getGeometry();

            if (geom != null && geom.getExtent() != null) {
                extendExtent(result, geom.getExtent());
            }
        }

        return result;
    }

    clearPrevious() {
        switch (this.currentAction) {
            case featureInteractionTypes.INSERT: this.clearInsert();
                break;
            case featureInteractionTypes.EDIT: this.clearEdit();
                break;
            case featureInteractionTypes.MOVE: this.clearMove();
                break;
        }
        this.currentAction = null;
    }

    clearInsert() {
        if (this.insertInteraction) {
            this.map.removeInteraction(this.insertInteraction);
            this.insertInteraction = null;
            this.previousAction = false;
            $event.notify(eventTypes.STOP_SNAP, {}, this, this.mapControl.classId);
        }
    }

    clearEdit() {
        if (this.editInteraction) {
            this.map.removeInteraction(this.editInteraction);
            this.editInteraction = null;

            if (this.selectInteraction) {
                this.map.removeInteraction(this.selectInteraction);
                this.selectInteraction = null;
            }

            this.previousAction = false;
            $event.notify(eventTypes.STOP_SNAP, {}, this, this.mapControl.classId);
        }
    }

    clearMove() {
        if (this.translateInteraction) {
            this.map.removeInteraction(this.translateInteraction);
            this.translateInteraction = null;
            if (this.selectInteraction) {
                this.map.removeInteraction(this.selectInteraction);
                this.selectInteraction = null;
            }
            this.previousAction = false;
            $event.notify(eventTypes.STOP_SNAP, {}, this, this.mapControl.classId);
        }
    }

    clear() {
        this.clearInsert();
        this.clearEdit();
        this.clearMove();

        if (this.previousAction) {
            this.clearPrevious();
        }

        if (this.editLayer != null) {
            this.editSource.clear();
            this.editLayer.dispose();
            this.map.removeLayer(this.editLayer);
            this.editLayer = null;
            this.editSource = null;
        }

        this.editMode = null;
        this.layerId = null;
        this.features = [];
        this.insertCallback = null;
        this.editCallback = null;
        this.oldBbox = null;
        this.initiated = false;
    }

    clearInteractions() {
        if (this.insertInteraction) {
            this.map.removeInteraction(this.insertInteraction);
            this.insertInteraction = null;
        }

        if (this.editInteraction) {
            this.map.removeInteraction(this.editInteraction);
            this.editInteraction = null;
        }
        if (this.translateInteraction) {
            this.map.removeInteraction(this.translateInteraction);
            this.translateInteraction = null;
        }

        if (this.selectInteraction) {
            this.map.removeInteraction(this.selectInteraction);
            this.selectInteraction = null;
        }
        this.previousAction = false;
        $event.notify(eventTypes.STOP_SNAP, {}, this, this.mapControl.classId);
    }

    clearTemporaryLayer() {
        if (this.editLayer != null) {
            this.editSource.clear();
            this.editLayer.dispose();
            this.map.removeLayer(this.editLayer);
            this.editLayer = null;
            this.editSource = null;
        }
    }

    createEditSource() {
        var result = new VectorSource({});

        for (var i = 0; i < this.features.length; i++) {
            this.features[i].set('xstate', 5);
            result.addFeature(this.features[i]);
        }

        return result;
    }

    createEditLayer(layerConfig) {
        if (this.editSource == null) {
            this.editSource = new VectorSource({});
        }

        let layerOptions = {
            source: this.editSource,
            map: this.map,
            style: this.handleStyleFunction(layerConfig),
            uid: layerTypes.EDITING
        };

        return new VectorLayer(layerOptions);
    }

    handleInsertStyleFunction() {
        return (feature) => {
            const type = feature.getGeometry().getType();

            if (type === 'LineString' || type === 'MultiLineString') {
                return this.lineStyle;
            }

            if (type === 'Polygon' || type === 'MultiPolygon') {
                return this.polygonStyle;
            }

            return this.lineStyle;
        }
    }

    _getLayerGeometryType(layerConfig, features) {
        if (layerConfig.Entity) {
            return layerConfig.Entity.GeometryFieldType;
        } else if (layerConfig.Type == layerTypes.TEMPORARY_VECTOR) {
            if (features == null || features.length == 0) {
                return 'POINT';
            } else {
                let geomType = features[0].getGeometry().getType();

                if (geomType == 'Point') {
                    return 'POINT';
                } else if (geomType == 'LineString' || layerConfig.Id == layerTypes.DIMENSIONING) {
                    return 'LINE';
                } else {
                    return 'POLYGON';
                }
            }
        }
    }

    handleStyleFunction(layerConfig) {
        let styleFunction = (feature) => {
            let styleId = styleTypes.SYSTEM_STYLE_EDIT_ALL;

            var geometryType = this._getLayerGeometryType(layerConfig, [feature]);

            if (geometryType == null || geometryType == '') {
                throw 'Config not found';
            }

            switch (geometryType) {
                case 'POINT': styleId = styleTypes.SYSTEM_STYLE_EDIT_POINT;
                    break;
                case 'LINE': styleId = styleTypes.SYSTEM_STYLE_EDIT_LINE;
                    break;
                case 'POLYGON': styleId = styleTypes.SYSTEM_STYLE_EDIT_POLYGON;
                    break;
                default: throw 'Geometry not supported';
            }

            var targetSld = this.mapControl.layerHandler.styleHandler.getStyleById('sys_' + styleId).style;

            targetSld.rules.forEach(rule => {
                if (rule.filter && rule.filter.predicates && rule.filter.predicates.length > 0) {
                    rule.filter.predicates.forEach(predicate => {
                        if (predicate.propertyname && predicate.propertyname == 'xstate') {
                            predicate.literal = 5;
                        }
                    });
                } else if (rule.filter) {
                    rule.filter.literal = 5;
                }
            });

            var style = createOlStyleFunction(targetSld, this.map);

            if (feature.get('hidden-state') == 1) {
                if (feature.get('modifyGeometry')) {
                    let modifyFeature = feature.clone();
                    modifyFeature.setGeometry(feature.get('modifyGeometry').geometry);
                    modifyFeature.unset('modifyGeometry');
                    let styles = style(modifyFeature);

                    styles[0].geometryFunction_ = () => {
                        return modifyFeature.getGeometry();
                    };
                    return styles;
                }

                return [];
            }

            feature.set('xstate', 5);

            if (feature.get('modifyGeometry')) {
                let modifyFeature = feature.clone();
                modifyFeature.setGeometry(feature.get('modifyGeometry').geometry);
                modifyFeature.unset('modifyGeometry');
                return style(modifyFeature);
            } else if (feature.get('features') != null && feature.get('features').length > 0) {
                return [];
            }

            return style(feature);
        }

        return styleFunction;

    }

    getOptionLayer(layerId) {
        return this.mapControl.layerSettingsHandler.getLayerSettings(layerId);
    }

    checkAction(action) {
        if (action == null) {
            if (this.editMode) {
                return featureInteractionTypes.EDIT;
            } else {
                return featureInteractionTypes.INSERT;
            }
        } else {
            return action;
        }
    }
}
